import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Minus, Plus, ShoppingBag } from 'lucide-react';
import { useCart } from '@/lib/cart';
import { useAuth } from '@/lib/auth';

export default function CartDrawer() {
    const { cart, open, setOpen, update } = useCart();
    const { type } = useAuth();
    const navigate = useNavigate();

    if (!open) return null;

    const checkout = () => {
        setOpen(false);
        if (type !== 'customer') { navigate('/login?next=/checkout'); return; }
        navigate('/checkout');
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="cart-drawer">
            <div className="absolute inset-0 bg-inorbit-blue-deeper/40" onClick={() => setOpen(false)} />
            <div className="relative bg-white w-full max-w-md h-full flex flex-col shadow-2xl">
                <div className="p-6 border-b border-border-subtle flex items-center justify-between">
                    <div>
                        <div className="overline">Cart</div>
                        <div className="font-serif text-2xl text-inorbit-blue mt-1">Your Bag ({cart.item_count})</div>
                    </div>
                    <button onClick={() => setOpen(false)} data-testid="cart-close-btn"><X size={22} /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                    {cart.items.length === 0 ? (
                        <div className="text-center py-16 text-text-tertiary">
                            <ShoppingBag size={48} strokeWidth={1} className="mx-auto mb-4 text-inorbit-yellow" />
                            <div className="text-sm">Your cart is empty</div>
                            <button onClick={() => { setOpen(false); navigate('/shop'); }} className="btn-link mt-4" data-testid="cart-empty-shop-btn">Start Shopping</button>
                        </div>
                    ) : (
                        <div className="space-y-5">
                            {cart.items.map((it) => (
                                <div key={it.product_id} className="flex gap-4 pb-5 border-b border-border-subtle" data-testid={`cart-item-${it.product_id}`}>
                                    <img src={it.image} alt="" className="w-20 h-24 object-cover" />
                                    <div className="flex-1">
                                        <div className="overline text-inorbit-orange text-[10px] mb-1">{it.brand_name}</div>
                                        <div className="text-sm text-inorbit-blue font-medium leading-snug">{it.name}</div>
                                        <div className="mt-2 flex items-center gap-2">
                                            <span className="text-sm font-semibold text-inorbit-blue">₹{it.price.toLocaleString('en-IN')}</span>
                                            {it.mrp > it.price && <span className="text-xs text-text-tertiary line-through">₹{it.mrp.toLocaleString('en-IN')}</span>}
                                        </div>
                                        <div className="mt-3 flex items-center gap-3">
                                            <div className="flex items-center border border-border-medium">
                                                <button className="px-2 py-1 hover:bg-cream" onClick={() => update(it.product_id, it.quantity - 1)} data-testid={`cart-dec-${it.product_id}`}><Minus size={12} /></button>
                                                <span className="px-3 text-sm">{it.quantity}</span>
                                                <button className="px-2 py-1 hover:bg-cream" onClick={() => update(it.product_id, it.quantity + 1)} data-testid={`cart-inc-${it.product_id}`}><Plus size={12} /></button>
                                            </div>
                                            <button className="text-xs text-text-tertiary hover:text-inorbit-orange" onClick={() => update(it.product_id, 0)} data-testid={`cart-remove-${it.product_id}`}>Remove</button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {cart.items.length > 0 && (
                    <div className="p-6 border-t border-border-subtle bg-cream">
                        <div className="space-y-2 text-sm mb-4">
                            <div className="flex justify-between"><span className="text-text-secondary">Subtotal</span><span>₹{cart.subtotal.toLocaleString('en-IN')}</span></div>
                            {cart.savings > 0 && <div className="flex justify-between text-inorbit-orange"><span>You save</span><span>− ₹{cart.savings.toLocaleString('en-IN')}</span></div>}
                            <div className="flex justify-between"><span className="text-text-secondary">Delivery</span><span>{cart.delivery === 0 ? 'FREE' : `₹${cart.delivery}`}</span></div>
                            <div className="flex justify-between"><span className="text-text-secondary">Tax</span><span>₹{cart.tax.toLocaleString('en-IN')}</span></div>
                        </div>
                        <div className="flex justify-between items-center pt-3 border-t border-border-medium">
                            <span className="text-sm uppercase tracking-widest text-text-tertiary">Total</span>
                            <span className="font-serif text-3xl text-inorbit-blue">₹{Math.round(cart.total).toLocaleString('en-IN')}</span>
                        </div>
                        <button onClick={checkout} className="btn-primary w-full justify-center mt-5" data-testid="cart-checkout-btn">Proceed to Checkout</button>
                    </div>
                )}
            </div>
        </div>
    );
}
